// ---------------------------------------------------------------------------
// The sync engine. Orchestrates push (drain outbox -> server) and pull (server
// -> apply locally), with:
//  - single-flight (no overlapping runs; a request during a run re-queues one)
//  - offline tolerance (network errors park as "offline", not "error")
//  - retry with backoff
//  - idempotent applies (triggers suppressed so applies don't re-queue)
//  - blobs synced out-of-band from JSON rows
// The engine talks to a SyncTransport, so it is fully testable with a fake.
// ---------------------------------------------------------------------------

import { persistNow } from '../db/database';
import { SYNCABLE_TABLES } from '../db/schema';
import {
  AuthError,
  OfflineError,
  type Conflict,
  type ChangePayload,
  type SyncTransport,
} from './client';
import {
  applyDocDelete,
  applyDocUpsert,
  clearOutboxThrough,
  countLocalConflicts,
  getCursor,
  getDeviceId,
  getDocRevision,
  isBlobEntity,
  isDocEntity,
  listLocalConflicts,
  markConflictResolved,
  nowIso,
  notifyApplied,
  outboxCount,
  readOutbox,
  serializeDoc,
  syncDb,
  setCloudUser,
  setCursor,
  setDocRevision,
  setSyncEnabled,
  upsertLocalConflict,
  withSuppressedTriggers,
} from './local';
import {
  applyBlobBytes,
  applyBlobMeta,
  blobRowExists,
  blobTypeFromMeta,
  clearPendingBlob,
  deleteBlobRow,
  listPendingBlobs,
  metaEntityType,
  readBlob,
  setPendingBlob,
} from './blobLocal';
import { setSyncStatus } from './status';

export type LinkMode = 'merge' | 'useCloud';

function platform(): string {
  return typeof (globalThis as { __TAURI__?: unknown }).__TAURI__ !== 'undefined' ? 'desktop' : 'web';
}

export class SyncEngine {
  private running = false;
  private queued = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private backoffMs = 0;
  private readonly persist: () => Promise<void>;

  constructor(
    private readonly transport: SyncTransport,
    opts: { persist?: () => Promise<void> } = {},
  ) {
    this.persist = opts.persist ?? (() => persistNow().catch(() => {}));
  }

  // --- Account linking (first sign-in flow) --------------------------------

  /**
   * Link the local database to the signed-in cloud account.
   *  - "merge": push all local rows up, then pull anything already in the cloud.
   *  - "useCloud": drop local syncable rows and adopt the cloud's data wholesale
   *    (machine-local settings like the Codex path are preserved).
   */
  async linkAccount(mode: LinkMode): Promise<void> {
    const boot = await this.transport.bootstrap(getDeviceId(), platform());
    setCloudUser(boot.userId, boot.email);

    if (mode === 'useCloud') {
      withSuppressedTriggers(() => {
        clearLocalSyncableData();
        setCursor(0);
      });
      setSyncEnabled(true);
      await this.pull();
    } else {
      setSyncEnabled(true);
      seedOutboxFromLocal();
      await this.runSync();
    }
    await this.persist();
    notifyApplied();
    this.refreshCounts();
  }

  /** Forget the cloud account locally (keeps all local data). */
  async unlink(): Promise<void> {
    setSyncEnabled(false);
    setCloudUser(null, null);
    setSyncStatus({ state: 'disabled', pending: 0, error: null });
    // sync_meta/sync_control writes bypass the repository's schedulePersist, so
    // flush explicitly — otherwise a refresh right after unlinking resurrects
    // the link. Notify so screens re-read link state.
    await this.persist();
    notifyApplied();
  }

  /**
   * Ask the server who the current auth token belongs to, without changing any
   * local link state. Used to (a) verify a re-sign-in matches the account this
   * device is linked to and (b) decide the hosted-web first-sync behavior.
   */
  bootstrapInfo(): Promise<import('./client').BootstrapResponse> {
    return this.transport.bootstrap(getDeviceId(), platform());
  }

  // --- Sync run (single-flight) --------------------------------------------

  /** Request a sync. Coalesces concurrent requests into one run. */
  async sync(): Promise<void> {
    if (this.running) {
      this.queued = true;
      return;
    }
    this.running = true;
    setSyncStatus({ state: 'syncing', error: null });
    try {
      await this.runSync();
      this.backoffMs = 0;
      setSyncStatus({
        state: countLocalConflicts() > 0 ? 'conflicted' : 'synced',
        lastSyncedAt: nowIso(),
        pending: outboxCount(),
        conflicts: countLocalConflicts(),
        error: null,
      });
    } catch (e) {
      if (e instanceof OfflineError) {
        setSyncStatus({ state: 'offline', error: null });
      } else if (e instanceof AuthError) {
        setSyncStatus({ state: 'error', error: 'Please sign in again.' });
      } else {
        setSyncStatus({ state: 'error', error: (e as Error).message });
        this.scheduleRetry();
      }
    } finally {
      await this.persist();
      this.running = false;
      if (this.queued) {
        this.queued = false;
        void this.sync();
      }
    }
  }

  private async runSync(): Promise<void> {
    await this.push();
    await this.pull();
    notifyApplied();
  }

  // --- Push ----------------------------------------------------------------

  private async push(): Promise<void> {
    const { maxSeq, changes } = readOutbox();
    if (changes.length === 0) return;

    const docChanges: ChangePayload[] = [];
    for (const ch of changes) {
      if (isBlobEntity(ch.entityType)) {
        const metaType = metaEntityType(ch.entityType);
        if (ch.op === 'delete') {
          docChanges.push({
            entityType: metaType, entityId: ch.entityId, op: 'delete', payload: null,
            baseRevision: getDocRevision(metaType, ch.entityId), updatedAt: nowIso(),
          });
          continue;
        }
        const blob = readBlob(ch.entityType, ch.entityId);
        if (!blob) {
          docChanges.push({
            entityType: metaType, entityId: ch.entityId, op: 'delete', payload: null,
            baseRevision: getDocRevision(metaType, ch.entityId), updatedAt: nowIso(),
          });
          continue;
        }
        await this.transport.uploadBlob(ch.entityType, ch.entityId, blob.bytes, blob.mime);
        docChanges.push({
          entityType: metaType, entityId: ch.entityId, op: 'upsert', payload: blob.metaPayload,
          baseRevision: getDocRevision(metaType, ch.entityId), updatedAt: nowIso(),
        });
      } else if (isDocEntity(ch.entityType)) {
        const payload = ch.op === 'delete' ? null : serializeDoc(ch.entityType, ch.entityId);
        if (ch.op === 'upsert' && payload === null) continue; // row vanished; a delete entry will follow
        docChanges.push({
          entityType: ch.entityType, entityId: ch.entityId,
          op: payload === null ? 'delete' : 'upsert', payload,
          baseRevision: getDocRevision(ch.entityType, ch.entityId), updatedAt: nowIso(),
        });
      }
    }

    if (docChanges.length === 0) {
      clearOutboxThrough(maxSeq);
      return;
    }

    const res = await this.transport.push({ deviceId: getDeviceId(), changes: docChanges });
    for (const r of res.results) {
      if (r.status === 'applied') setDocRevision(r.entityType, r.entityId, r.revision);
    }
    for (const c of res.conflicts) mirrorConflict(c);
    clearOutboxThrough(maxSeq);
  }

  // --- Pull ----------------------------------------------------------------

  private async pull(): Promise<void> {
    let cursor = getCursor();
    // Applying rows out of dependency order (a child before its parent, or a row
    // whose parent was already deleted) would trip foreign keys. Sync is the one
    // place that legitimately writes rows in arbitrary order, so FK enforcement
    // is relaxed for the apply; tombstones clean up any orphans.
    syncDb().run('PRAGMA foreign_keys=OFF');
    try {
      for (let guard = 0; guard < 1000; guard++) {
        const res = await this.transport.pull(cursor);
        // Apply JSON docs (including blob meta docs) before blob bytes, so the
        // row a blob attaches to already exists when its bytes arrive.
        const ordered = [...res.changes].sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'blob' ? 1 : -1));
        for (const change of ordered) {
          await this.applyChange(change);
        }
        cursor = res.cursor;
        setCursor(cursor);
        if (!res.hasMore) break;
      }
    } finally {
      syncDb().run('PRAGMA foreign_keys=ON');
    }
    // Blob bytes whose meta doc arrived after them (later page or earlier run)
    // are queued in sync_pending_blob; now that all docs in this pull have been
    // applied, fill in every queued blob whose row exists.
    await this.drainPendingBlobs();
  }

  /** Download queued blob bytes whose local row (from the meta doc) now exists. */
  private async drainPendingBlobs(): Promise<void> {
    for (const p of listPendingBlobs()) {
      if (!blobRowExists(p.entityType, p.entityId)) continue; // meta still missing; retry next pull
      const dl = await this.transport.downloadBlob(p.blobId);
      if (dl) {
        withSuppressedTriggers(() => applyBlobBytes(p.entityType, p.entityId, dl.bytes, dl.mime));
        await runPostApplyHook(p.entityType, p.entityId);
      }
      // Clear even when the download returned nothing: the server no longer has
      // the blob, so retrying forever would never succeed.
      clearPendingBlob(p.entityType, p.entityId);
    }
  }

  private async applyChange(change: import('./client').DocumentChange): Promise<void> {
    const { entityType, entityId, revision, deleted, kind, payload } = change;

    if (kind === 'blob' && change.blob) {
      if (deleted) {
        withSuppressedTriggers(() => deleteBlobRow(entityType, entityId));
        clearPendingBlob(entityType, entityId);
      } else if (blobRowExists(entityType, entityId)) {
        const dl = await this.transport.downloadBlob(change.blob.blobId);
        if (dl) {
          withSuppressedTriggers(() => applyBlobBytes(entityType, entityId, dl.bytes, dl.mime));
          await runPostApplyHook(entityType, entityId);
        }
        clearPendingBlob(entityType, entityId);
      } else {
        // The meta doc that creates this row hasn't been pulled yet (it can sit
        // pages behind, since blob uploads take their own earlier revision).
        // Queue the download; drainPendingBlobs retries it once the row exists.
        setPendingBlob(entityType, entityId, change.blob.blobId);
      }
      setDocRevision(entityType, entityId, revision);
      return;
    }

    const blobType = blobTypeFromMeta(entityType);
    if (blobType) {
      withSuppressedTriggers(() => {
        if (deleted) deleteBlobRow(blobType, entityId);
        else if (payload) applyBlobMeta(blobType, payload);
      });
      setDocRevision(entityType, entityId, revision);
      return;
    }

    if (isDocEntity(entityType)) {
      withSuppressedTriggers(() => {
        if (deleted) applyDocDelete(entityType, entityId);
        else if (payload) applyDocUpsert(entityType, payload);
      });
      setDocRevision(entityType, entityId, revision);
    }
  }

  // --- Conflict resolution -------------------------------------------------

  listConflicts() {
    return listLocalConflicts();
  }

  async resolveConflict(conflictId: string, resolution: 'mine' | 'cloud' | 'merge', mergedPayload: string | null): Promise<void> {
    // Retrying is safe: a re-sent resolution for an already-resolved conflict is a
    // server no-op (returns empty results), so this survives a cold start without
    // double-applying. See withRetry for why the first call can transiently fail.
    const res = await this.withRetry(() => this.transport.resolveConflict({ conflictId, resolution, mergedPayload }));
    // The server applied the resolution and bumped the revision; record it, then
    // pull to adopt the authoritative row so every device converges.
    for (const r of res.results) {
      if (r.status === 'applied') setDocRevision(r.entityType, r.entityId, r.revision);
    }
    markConflictResolved(conflictId);
    // The resolution is already committed server-side. Pull to adopt the
    // authoritative row now, but a pull hiccup must NOT read back as a failed
    // resolution — the periodic sync loop will converge the row on its next run.
    try {
      await this.withRetry(() => this.pull());
    } catch {
      /* resolved server-side already; convergence deferred to the next sync */
    }
    await this.persist();
    notifyApplied();
    this.refreshCounts();
  }

  /**
   * Retry a user-initiated network call through a transient failure. The hosted
   * API runs on Azure Container Apps with scale-to-zero, so the first request
   * after an idle spell (e.g. while the user reads the conflict dialog) can hit a
   * cold start: the ingress answers with a 5xx that carries no CORS headers, which
   * the browser surfaces to fetch as a bare "Load failed" / "Failed to fetch"
   * (an OfflineError here). A short backoff lets the container wake and the call
   * succeed. Auth failures are not retried — a fresh token won't appear on retry.
   */
  private async withRetry<T>(fn: () => Promise<T>, attempts = 4): Promise<T> {
    let lastErr: unknown;
    for (let i = 0; i < attempts; i++) {
      try {
        return await fn();
      } catch (e) {
        if (e instanceof AuthError) throw e;
        lastErr = e;
        if (i < attempts - 1) await delay(500 * 2 ** i); // 0.5s, 1s, 2s
      }
    }
    if (lastErr instanceof OfflineError) {
      throw new Error('Could not reach the sync server (it may be waking up). Please try again in a moment.');
    }
    throw lastErr as Error;
  }

  // --- Scheduling ----------------------------------------------------------

  /** Debounced sync trigger, called after local writes. */
  schedule(delayMs = 1500): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.sync();
    }, delayMs);
  }

  private scheduleRetry(): void {
    this.backoffMs = Math.min(this.backoffMs ? this.backoffMs * 2 : 5000, 5 * 60_000);
    setTimeout(() => void this.sync(), this.backoffMs);
  }

  refreshCounts(): void {
    setSyncStatus({ pending: outboxCount(), conflicts: countLocalConflicts() });
  }
}

// --- module helpers ---------------------------------------------------------

const delay = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/**
 * Derived-data rebuild after blob bytes land. profile_document.text is NOT part
 * of the synced meta payload (it can exceed the server's doc size cap), so the
 * receiving device re-extracts it from the downloaded bytes. Best-effort.
 */
async function runPostApplyHook(entityType: string, entityId: string): Promise<void> {
  if (entityType !== 'profile_document') return;
  try {
    const { reextractProfileDocumentText } = await import('./postApply');
    await reextractProfileDocumentText(entityId);
  } catch {
    // never let a derived-data rebuild fail the sync run
  }
}

function mirrorConflict(c: Conflict): void {
  upsertLocalConflict({
    id: c.id,
    entityType: c.entityType,
    entityId: c.entityId,
    baseRevision: c.baseRevision,
    serverRevision: c.serverRevision,
    serverPayload: c.serverPayload,
    clientPayload: c.clientPayload,
    createdAt: c.createdAt,
  });
}

/** Enqueue every existing syncable row for upload (initial "merge" link). */
function seedOutboxFromLocal(): void {
  const db = syncDb();
  for (const t of SYNCABLE_TABLES) {
    db.run(
      `INSERT INTO sync_outbox (entity_type, entity_id, op)
       SELECT '${t.table}', ${t.pk}, 'upsert' FROM ${t.table}`,
    );
  }
}

/** Drop local syncable rows (for "useCloud"), preserving machine-local settings. */
function clearLocalSyncableData(): void {
  const db = syncDb();
  for (const t of SYNCABLE_TABLES) {
    if (t.table === 'app_settings') continue; // keep local machine settings
    db.run(`DELETE FROM ${t.table}`);
  }
  db.run('DELETE FROM sync_doc_state');
  db.run('DELETE FROM sync_outbox');
  db.run('DELETE FROM sync_pending_blob');
}
